/**
 * SettingsPage.jsx - AGGIORNATO
 * 
 * MODIFICHE:
 * - Rimosso tab Aspetto (branding)
 * - Layout accordion su mobile (<768px), tutte le sezioni chiuse all'apertura
 * - Ordine sezioni: Calendario, Backup, Campi Form, Dati Centro
 * - 4 Card in Dati Centro: Informazioni, Indirizzo, Contatti, Dati Fiscali
 */

import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { useNotification } from '../../contexts/NotificationContext'
import { settingsApi } from '../../services/api'
import { PageLoader, Card, Toggle } from '../../components/ui'
import { 
  Settings, FileText, Save, Loader2, Plus, X, Trash2, Edit2, Eye,
  MessageSquare, Calendar as CalendarIcon, Cloud, Building2, MapPin,
  Phone, Receipt, ChevronDown, ChevronUp, Check, Sliders, Zap, Lock
} from 'lucide-react'
import CalendarSettingsTab from '../../components/settings/CalendarSettingsTab'
import GoogleDriveSettingsTab from '../../components/settings/GoogleDriveSettingsTab'
import { useAllFeatures, FEATURE_META } from '../../hooks/useFeature'

export default function SettingsPage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState('calendario')
  const [settings, setSettings] = useState(null)
  // Stato per accordion mobile
  const [expandedSection, setExpandedSection] = useState(null)
  
  const { tenant, updateTenant, user } = useAuth()
  const { setColors } = useTheme()
  const { showSuccess, showError } = useNotification()

  const plan = tenant?.currentPlan || 'trial'
  const isStarter = plan === 'starter' || plan === 'trial'
  const isProfessional = plan === 'professional'
  const isEnterprise = plan === 'enterprise'

  useEffect(() => { loadSettings() }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const res = await settingsApi.get()
      setSettings(res.data.data || res.data)
    } catch (error) {
      showError('Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await settingsApi.update(settings)
      showSuccess('Impostazioni salvate')
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <PageLoader />

  const tabs = [
    { id: 'calendario', label: 'Calendario', icon: CalendarIcon },
    { id: 'funzionalita', label: 'Funzionalità', icon: Zap },
    { id: 'backup', label: 'Backup', icon: Cloud },
    { id: 'fields', label: 'Campi Form', icon: FileText },
    { id: 'centro', label: 'Dati Centro', icon: Building2 }
  ]

  // Toggle accordion mobile
  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId)
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Settings className="h-7 w-7 mr-2 text-primary-600" />Impostazioni
        </h1>
        <button onClick={handleSave} disabled={saving} className={`btn-primary flex items-center ${activeTab === 'centro' ? 'invisible' : ''}`}>
          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Salvataggio...</> : <><Save className="h-4 w-4 mr-2" />Salva</>}
        </button>
      </div>

      {/* === DESKTOP: Tabs orizzontali (>= 768px) === */}
      <div className="hidden md:block">
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
          {tabs.map(tab => (
            <button 
              key={tab.id} 
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' 
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />{tab.label}
            </button>
          ))}
        </div>

        {/* Content Desktop */}
        <div className="mt-6">
          {activeTab === 'centro' && (
            <CentroSettings settings={settings} />
          )}
          {activeTab === 'fields' && (
            <FieldsSettings 
              settings={settings} 
              setSettings={setSettings} 
              plan={plan}
              isStarter={isStarter}
              isProfessional={isProfessional}
              isEnterprise={isEnterprise}
              showSuccess={showSuccess}
              showError={showError}
            />
          )}
          {activeTab === 'calendario' && (
            <CalendarSettingsTab
              settings={settings}
              setSettings={setSettings}
              showSuccess={showSuccess}
              showError={showError}
            />
          )}
          {activeTab === 'funzionalita' && (
            <FeaturesSettings
              settings={settings}
              setSettings={setSettings}
              tenant={tenant}
              showSuccess={showSuccess}
              showError={showError}
            />
          )}
          {activeTab === 'backup' && (
            <GoogleDriveSettingsTab 
              showSuccess={showSuccess}
              showError={showError}
            />
          )}
        </div>
      </div>

      {/* === MOBILE: Accordion verticale (< 768px) === */}
      <div className="md:hidden space-y-3">
        {tabs.map(tab => (
          <div key={tab.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
            {/* Header Accordion */}
            <button
              onClick={() => toggleSection(tab.id)}
              className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
                expandedSection === tab.id 
                  ? 'bg-primary-50 dark:bg-primary-900/20 border-b border-primary-100 dark:border-primary-800' 
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${
                  expandedSection === tab.id 
                    ? 'bg-primary-100 dark:bg-primary-800' 
                    : 'bg-gray-100 dark:bg-gray-700'
                }`}>
                  <tab.icon className={`h-5 w-5 ${
                    expandedSection === tab.id 
                      ? 'text-primary-600 dark:text-primary-400' 
                      : 'text-gray-500 dark:text-gray-400'
                  }`} />
                </div>
                <span className={`font-medium ${
                  expandedSection === tab.id 
                    ? 'text-primary-900 dark:text-primary-100' 
                    : 'text-gray-900 dark:text-white'
                }`}>
                  {tab.label}
                </span>
              </div>
              {expandedSection === tab.id 
                ? <ChevronUp className="h-5 w-5 text-primary-500" />
                : <ChevronDown className="h-5 w-5 text-gray-400" />
              }
            </button>
            
            {/* Content Accordion */}
            {expandedSection === tab.id && (
              <div className="p-4">
                {tab.id === 'centro' && (
                  <CentroSettings settings={settings} />
                )}
                {tab.id === 'fields' && (
                  <FieldsSettings 
                    settings={settings} 
                    setSettings={setSettings} 
                    plan={plan}
                    isStarter={isStarter}
                    isProfessional={isProfessional}
                    isEnterprise={isEnterprise}
                    showSuccess={showSuccess}
                    showError={showError}
                  />
                )}
                {tab.id === 'calendario' && (
                  <CalendarSettingsTab
                    settings={settings}
                    setSettings={setSettings}
                    showSuccess={showSuccess}
                    showError={showError}
                  />
                )}
                {tab.id === 'funzionalita' && (
                  <FeaturesSettings
                    settings={settings}
                    setSettings={setSettings}
                    tenant={tenant}
                    showSuccess={showSuccess}
                    showError={showError}
                  />
                )}
                {tab.id === 'backup' && (
                  <GoogleDriveSettingsTab 
                    showSuccess={showSuccess}
                    showError={showError}
                  />
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// Metadati per ogni regola Smart Alert
const ALERT_RULES_META = {
  R03: { label: 'Assenze consecutive', desc: 'Scatta dopo 3+ giorni assenti di fila', severity: 'critical' },
  R04: { label: 'Segnalazione urgente', desc: 'Entry contrassegnata come urgente — dismiss permanente', severity: 'critical' },
  R05: { label: 'Bassa frequenza', desc: 'Frequenza < 50% nelle ultime 2 settimane', severity: 'warning' },
  R06: { label: 'Compleanno imminente', desc: 'Compleanno entro 7 giorni', severity: 'info' },
  R07: { label: 'Accompagnatori variabili', desc: '5+ accompagnatori diversi negli ultimi 7 giorni', severity: 'info' },
  R08: { label: 'Peggioramento comportamento', desc: 'Episodi +100% rispetto alla settimana precedente', severity: 'warning' },
  R09: { label: 'Trend positivo', desc: 'Presente e compiti ok per 5+ giorni consecutivi', severity: 'info' },
  R11: { label: 'Senza educatore referente', desc: 'Minore senza referente assegnato', severity: 'info' },
  R20: { label: 'Entry incomplete', desc: 'Entry mancanti 24h dopo la chiusura del centro', severity: 'warning' },
}

const severityColors = {
  critical: 'text-red-500',
  warning: 'text-amber-500',
  info: 'text-blue-500',
}

// === TAB FUNZIONALITÀ ===
function FeaturesSettings({ settings, setSettings, tenant, showSuccess, showError }) {
  const allFeatures = useAllFeatures()
  const [saving, setSaving] = useState(false)
  const [savingAlert, setSavingAlert] = useState(false)
  const enabledFeatures = settings?.enabledFeatures || {}

  // Stato locale per alertSettings (merge con default)
  const defaultAlertSettings = Object.fromEntries(
    Object.keys(ALERT_RULES_META).map(id => [id, {
      active: true,
      visibileACoordinatore: !['R06', 'R09'].includes(id),
      visibileAEducatori: ['R04', 'R06', 'R09', 'R20'].includes(id),
    }])
  )
  const [alertSettings, setAlertSettings] = useState(() => ({
    ...defaultAlertSettings,
    ...(settings?.alertSettings || {})
  }))

  const handleToggle = async (featureKey, newValue) => {
    setSaving(true)
    try {
      const updated = { ...enabledFeatures, [featureKey]: newValue }
      await settingsApi.update({ enabledFeatures: updated })
      setSettings(prev => ({ ...prev, enabledFeatures: updated }))
      showSuccess(`${FEATURE_META[featureKey]?.label || featureKey} ${newValue ? 'abilitata' : 'disabilitata'}`)
    } catch (error) {
      showError('Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handleAlertRuleChange = async (ruleId, field, value) => {
    const updated = {
      ...alertSettings,
      [ruleId]: { ...alertSettings[ruleId], [field]: value }
    }
    setAlertSettings(updated)
    setSavingAlert(true)
    try {
      await settingsApi.update({ alertSettings: updated })
      setSettings(prev => ({ ...prev, alertSettings: updated }))
    } catch {
      showError('Errore nel salvataggio regole alert')
    } finally {
      setSavingAlert(false)
    }
  }

  const smartAlertsEnabled = enabledFeatures.smartAlerts !== false
  const smartAlertsLocked = allFeatures.smartAlerts?.reason === 'global' || allFeatures.smartAlerts?.reason === 'plan'

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Funzionalità del centro</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Abilita o disabilita le funzionalità per tutti gli utenti del centro
        </p>
      </div>

      <div className="space-y-3">
        {Object.entries(FEATURE_META).map(([key, meta]) => {
          const feature = allFeatures[key]
          const isEnabled = enabledFeatures[key] !== false
          const isLocked = feature?.reason === 'global' || feature?.reason === 'plan'

          return (
            <Card key={key}>
              <div className="flex items-center justify-between">
                <div className={`flex items-center flex-1 mr-3 ${isLocked ? 'opacity-60' : ''}`}>
                  <span className="text-xl mr-3">{meta.icon}</span>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{meta.label}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{meta.desc}</p>
                    {meta.roles && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        Visibile a: {meta.roles.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
                {isLocked ? (
                  <div className="flex items-center text-sm text-gray-400">
                    <Lock className="h-4 w-4 mr-1" />
                    <span>{feature.lockMessage}</span>
                  </div>
                ) : (
                  <Toggle
                    enabled={isEnabled}
                    onChange={(val) => handleToggle(key, val)}
                    disabled={saving}
                  />
                )}
              </div>

              {/* Pannello espandibile regole Smart Alerts */}
              {key === 'smartAlerts' && isEnabled && !isLocked && (
                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Impostazioni regole
                  </p>

                  <div className="space-y-2">
                    {Object.entries(ALERT_RULES_META).map(([ruleId, ruleMeta]) => {
                      const rule = alertSettings[ruleId] || { active: true, visibileACoordinatore: true, visibileAEducatori: false }
                      const colorClass = severityColors[ruleMeta.severity] || 'text-gray-400'

                      return (
                        <div key={ruleId} className={`flex items-center gap-3 py-2 px-3 rounded-lg transition-opacity ${rule.active ? 'bg-gray-50 dark:bg-gray-700/50' : 'opacity-40'}`}>
                          {/* Mini toggle attiva */}
                          <Toggle
                            enabled={rule.active}
                            onChange={(val) => handleAlertRuleChange(ruleId, 'active', val)}
                            disabled={savingAlert}
                          />
                          {/* Testo */}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${colorClass}`}>{ruleMeta.label}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{ruleMeta.desc}</p>
                          </div>
                          {/* Pill coordinatore */}
                          <button
                            onClick={() => rule.active && handleAlertRuleChange(ruleId, 'visibileACoordinatore', !rule.visibileACoordinatore)}
                            disabled={!rule.active || savingAlert}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                              !rule.active ? 'bg-gray-100 text-gray-300 dark:bg-gray-800 dark:text-gray-600 cursor-not-allowed' :
                              rule.visibileACoordinatore
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            Coord
                          </button>
                          {/* Pill educatore */}
                          <button
                            onClick={() => rule.active && handleAlertRuleChange(ruleId, 'visibileAEducatori', !rule.visibileAEducatori)}
                            disabled={!rule.active || savingAlert}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                              !rule.active ? 'bg-gray-100 text-gray-300 dark:bg-gray-800 dark:text-gray-600 cursor-not-allowed' :
                              rule.visibileAEducatori
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            Edu
                          </button>
                        </div>
                      )
                    })}
                  </div>

                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                    Toggle = Attiva/Disattiva · <span className="text-green-600">Coord</span> = Visibile al coordinatore · <span className="text-green-600">Edu</span> = Visibile agli educatori
                  </p>
                </div>
              )}
            </Card>
          )
        })}
      </div>
    </div>
  )
}

// === TAB DATI CENTRO ===
function CentroSettings({ settings }) {
  const centro = settings?.centro || {}
  const orgFiscal = settings?.organizationFiscal || null

  // Helper per visualizzare campo con cascata
  const renderFiscalField = (label, centerValue, orgValue, helperText) => {
    const hasCustom = centerValue && centerValue.trim() !== ''
    const hasOrg = orgValue && orgValue.trim() !== ''
    const displayValue = hasCustom ? centerValue : (hasOrg ? orgValue : '')
    
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
        <input 
          type="text" 
          value={displayValue} 
          readOnly
          className="input bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
        />
        {hasCustom ? (
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">Personalizzato per questo centro</p>
        ) : hasOrg ? (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 italic">Ereditato dall'organizzazione</p>
        ) : (
          <p className="text-xs text-gray-500 mt-1">{helperText}</p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Banner informativo */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
        <svg className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
        </svg>
        <div>
          <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Dati in sola lettura</p>
          <p className="text-sm text-blue-600 dark:text-blue-400 mt-0.5">
            I dati del centro vengono gestiti dal Responsabile dell'organizzazione.
          </p>
        </div>
      </div>
      {/* Card 1: Informazioni Base */}
      <Card>
        <div className="flex items-center space-x-2 mb-4">
          <Building2 className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Informazioni</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nome Centro
            </label>
            <input 
              type="text" 
              value={centro.name || ''} 
              disabled
              className="input bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">Il nome non può essere modificato</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tipo Ente
            </label>
            <input 
              type="text" 
              value={
                centro.entityType === 'onlus' ? 'ONLUS / ETS' :
                centro.entityType === 'cooperativa' ? 'Cooperativa Sociale' :
                centro.entityType === 'pubblico' ? 'Ente Pubblico' :
                centro.entityType === 'privato' ? 'Privato' : 
                centro.entityType || ''
              } 
              disabled
              className="input bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
            />
          </div>
          <div className="md:col-span-2">
            {renderFiscalField(
              'Ragione Sociale',
              centro.billing?.businessName,
              orgFiscal?.businessName,
              'Nome legale completo dell\'organizzazione'
            )}
          </div>
        </div>
      </Card>

      {/* Card 2: Indirizzo */}
      <Card>
        <div className="flex items-center space-x-2 mb-4">
          <MapPin className="h-5 w-5 text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Indirizzo</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Via / Indirizzo
            </label>
            <input 
              type="text" 
              value={centro.address?.street || ''} 
              readOnly
              className="input bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Città
            </label>
            <input 
              type="text" 
              value={centro.address?.city || ''} 
              readOnly
              className="input bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Provincia
              </label>
              <input 
                type="text" 
                value={centro.address?.province || ''} 
                readOnly
                maxLength={2}
                className="input uppercase bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                CAP
              </label>
              <input 
                type="text" 
                value={centro.address?.postalCode || ''} 
                readOnly
                maxLength={5}
                className="input bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Card 3: Contatti */}
      <Card>
        <div className="flex items-center space-x-2 mb-4">
          <Phone className="h-5 w-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Contatti</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Telefono
            </label>
            <input 
              type="tel" 
              value={centro.contacts?.phone || ''} 
              readOnly
              className="input bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email
            </label>
            <input 
              type="email" 
              value={centro.contacts?.email || ''} 
              readOnly
              className="input bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Sito Web
            </label>
            <input 
              type="url" 
              value={centro.contacts?.website || ''} 
              readOnly
              className="input bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
            />
          </div>
        </div>
      </Card>

      {/* Card 4: Dati Fiscali */}
      <Card>
        <div className="flex items-center space-x-2 mb-4">
          <Receipt className="h-5 w-5 text-amber-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Dati Fiscali</h2>
        </div>
        {orgFiscal && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              I dati non compilati per questo centro vengono ereditati automaticamente dall'organizzazione.
            </p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderFiscalField(
            'Partita IVA',
            centro.billing?.vatNumber,
            orgFiscal?.vatNumber,
            '11 cifre'
          )}
          {renderFiscalField(
            'Codice Fiscale',
            centro.billing?.fiscalCode,
            orgFiscal?.fiscalCode,
            'Codice fiscale dell\'ente'
          )}
          {renderFiscalField(
            'PEC',
            centro.billing?.pecEmail,
            orgFiscal?.pecEmail,
            'Posta Elettronica Certificata'
          )}
          {renderFiscalField(
            'Codice SDI',
            centro.billing?.sdiCode,
            orgFiscal?.sdiCode,
            'Codice Destinatario per fatture elettroniche (7 caratteri)'
          )}
        </div>
      </Card>
    </div>
  )
}

// === OPZIONI PREDEFINITE PER CAMPI STANDARD ===
// Usate come fallback e per il pulsante "Ripristina default"
const FALLBACK_OPTIONS = {
  comportamentiProblematici: [
    { value: 'positivo', label: '✅ Positivo' },
    { value: 'aggressivo_se', label: 'Aggressivo verso sé' },
    { value: 'aggressivo_pari', label: 'Aggressivo verso pari' },
    { value: 'aggressivo_adulti', label: 'Aggressivo verso adulti' },
    { value: 'oppositivo_pari', label: 'Oppositivo verso pari' },
    { value: 'oppositivo_adulti', label: 'Oppositivo verso adulti' }
  ],
  svolgimentoCompiti: [
    { value: 'autonomo', label: '✅ Autonomo' },
    { value: 'richiede_aiuto', label: 'Richiede aiuto' },
    { value: 'svolge_con_piacere', label: 'Svolge con piacere' },
    { value: 'indifferente', label: 'Indifferente' },
    { value: 'si_rifiuta', label: 'Si rifiuta' },
    { value: 'ha_materiale', label: 'Ha materiale' },
    { value: 'no_materiale', label: 'No materiale' },
    { value: 'no_compiti', label: 'No compiti' }
  ],
  attivitaGruppo: [
    { value: 'partecipa_attivamente', label: 'Partecipa attivamente' },
    { value: 'collabora', label: 'Collabora' },
    { value: 'cerca_di_prevalere', label: 'Cerca di prevalere' },
    { value: 'passivo', label: 'Passivo' },
    { value: 'escluso', label: 'Escluso' },
    { value: 'ricercato', label: 'Ricercato' }
  ],
  tempoLibero: [
    { value: 'propone_attivita', label: 'Propone attività' },
    { value: 'accetta_proposte_compagni', label: 'Accetta proposte compagni' },
    { value: 'accetta_proposte_educatori', label: 'Accetta proposte educatori' },
    { value: 'passivo', label: 'Passivo' },
    { value: 'altro', label: 'Altro' }
  ]
}

// === TAB CAMPI FORM ===
function FieldsSettings({ settings, setSettings, plan, isStarter, isProfessional, isEnterprise, showSuccess, showError }) {
  const [sectionModal, setSectionModal] = useState(null)
  const [sectionForm, setSectionForm] = useState({ name: '', options: [], notes: [] })
  const [tempOptionInput, setTempOptionInput] = useState('')
  const [tempNoteInput, setTempNoteInput] = useState('')
  
  // Stato per modal sezioni standard
  const [standardModal, setStandardModal] = useState(null)
  const [standardOptions, setStandardOptions] = useState([])
  const [tempStandardInput, setTempStandardInput] = useState('')
  const [savingStandard, setSavingStandard] = useState(false)
  const [savingSection, setSavingSection] = useState(false)

  const customSections = settings?.customSections || []
  const maxSections = 3
  const maxOptionsPerField = 5

  // Definizione sezioni standard
  const standardSections = [
    { 
      id: 'comportamentiProblematici', 
      label: 'Comportamento', 
      field: 'comportamentiOptions',
      icon: '😊',
      description: 'Opzioni per il campo comportamento'
    },
    { 
      id: 'svolgimentoCompiti', 
      label: 'Svolgimento Compiti', 
      field: 'compitiOptions',
      icon: '📚',
      description: 'Opzioni per il campo compiti'
    },
    { 
      id: 'attivitaGruppo', 
      label: 'Attività di Gruppo', 
      field: 'attivitaGruppoOptions',
      icon: '👥',
      description: 'Opzioni per le attività di gruppo'
    },
    { 
      id: 'tempoLibero', 
      label: 'Tempo Libero', 
      field: 'tempoLiberoOptions',
      icon: '🎮',
      description: 'Opzioni per il tempo libero'
    }
  ]

  // Ottieni opzioni correnti per una sezione standard
  const getStandardOptions = (fieldId) => {
    const section = standardSections.find(s => s.id === fieldId)
    if (!section) return []
    return settings?.fieldOptions?.[fieldId] || []
  }

  // Apri modal per modificare sezione standard
  const openStandardModal = (section) => {
    const currentOptions = getStandardOptions(section.id)
    setStandardOptions(currentOptions.map((opt, i) => 
      typeof opt === 'string' ? opt : opt.label || opt
    ))
    setStandardModal(section)
    setTempStandardInput('')
  }

  // Aggiungi opzione a sezione standard
  const addStandardOption = () => {
    if (!tempStandardInput.trim() || standardOptions.length >= maxOptionsPerField) return
    setStandardOptions(prev => [...prev, tempStandardInput.trim()])
    setTempStandardInput('')
  }

  // Rimuovi opzione da sezione standard
  const removeStandardOption = (index) => {
    setStandardOptions(prev => prev.filter((_, i) => i !== index))
  }

  // Salva sezione standard - chiamata API diretta
  const saveStandardSection = async () => {
    if (!standardModal) return
    
    // Se c'è testo nell'input, aggiungilo automaticamente
    let finalOptions = [...standardOptions]
    if (tempStandardInput.trim() && finalOptions.length < maxOptionsPerField) {
      finalOptions.push(tempStandardInput.trim())
    }
    
    // Prepara i nuovi settings con le opzioni aggiornate
    const updatedSettings = {
      ...settings,
      fieldOptions: {
        ...settings.fieldOptions,
        [standardModal.id]: finalOptions
      }
    }
    
    setSavingStandard(true)
    try {
      await settingsApi.update(updatedSettings)
      
      // Aggiorna stato locale
      setSettings(updatedSettings)
      
      showSuccess(`Opzioni "${standardModal.label}" salvate`)
      setStandardModal(null)
      setStandardOptions([])
      setTempStandardInput('')
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSavingStandard(false)
    }
  }

  // Ripristina opzioni predefinite per sezione standard
  const resetToDefaults = () => {
    if (!standardModal) return
    const defaults = FALLBACK_OPTIONS[standardModal.id] || []
    setStandardOptions(defaults.map(opt => opt.label))
  }

  // Funzioni per sezioni personalizzate (esistenti)
  const getTotalElements = () => sectionForm.options.length + sectionForm.notes.length
  const getMaxNotes = () => Math.max(0, 6 - sectionForm.options.length)

  const addSectionOption = (label) => {
    if (!label?.trim() || sectionForm.options.length >= 5 || getTotalElements() >= 6) return
    setSectionForm(prev => ({
      ...prev,
      options: [...prev.options, { id: `opt_${Date.now()}`, label: label.trim(), linkedNote: '' }]
    }))
  }

  const removeSectionOption = (id) => {
    setSectionForm(prev => ({
      ...prev,
      options: prev.options.filter(o => o.id !== id)
    }))
  }

  const toggleLinkedNote = (optionId) => {
    setSectionForm(prev => ({
      ...prev,
      options: prev.options.map(opt => 
        opt.id === optionId 
          ? { ...opt, linkedNote: opt.linkedNote ? '' : 'Note' }
          : opt
      )
    }))
  }

  const updateLinkedNoteLabel = (optionId, label) => {
    setSectionForm(prev => ({
      ...prev,
      options: prev.options.map(opt => 
        opt.id === optionId 
          ? { ...opt, linkedNote: label }
          : opt
      )
    }))
  }

  const addSectionNote = (label) => {
    if (!label?.trim() || sectionForm.notes.length >= getMaxNotes() || getTotalElements() >= 6) return
    setSectionForm(prev => ({
      ...prev,
      notes: [...prev.notes, { id: `note_${Date.now()}`, label: label.trim() }]
    }))
  }

  const removeSectionNote = (id) => {
    setSectionForm(prev => ({
      ...prev,
      notes: prev.notes.filter(n => n.id !== id)
    }))
  }

  const openCreateModal = () => {
    setSectionForm({ name: '', options: [], notes: [] })
    setSectionModal({ mode: 'create' })
  }

  const openEditModal = (index) => {
    const section = customSections[index]
    setSectionForm({
      name: section.name || '',
      options: section.options || [],
      notes: section.notes || []
    })
    setSectionModal({ mode: 'edit', index })
  }

  const saveSection = async () => {
    if (!sectionForm.name.trim()) return
    if (sectionForm.options.length === 0 && sectionForm.notes.length === 0) return

    const newSection = {
      id: sectionModal.mode === 'edit' && customSections[sectionModal.index]?.id 
        ? customSections[sectionModal.index].id 
        : `section_${Date.now()}`,
      name: sectionForm.name.trim(),
      options: sectionForm.options,
      notes: sectionForm.notes
    }

    let updated
    if (sectionModal.mode === 'create') {
      updated = [...customSections, newSection]
    } else {
      updated = customSections.map((s, i) => i === sectionModal.index ? newSection : s)
    }

    const updatedSettings = { ...settings, customSections: updated }
    
    setSavingSection(true)
    try {
      await settingsApi.update(updatedSettings)
      
      setSettings(updatedSettings)
      showSuccess(sectionModal.mode === 'create' 
        ? `Sezione "${newSection.name}" creata` 
        : `Sezione "${newSection.name}" aggiornata`)
      
      setSectionModal(null)
      setSectionForm({ name: '', options: [], notes: [] })
      setTempOptionInput('')
      setTempNoteInput('')
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSavingSection(false)
    }
  }

  const deleteSection = (index) => {
    if (!confirm('Eliminare questa sezione?')) return
    const updated = customSections.filter((_, i) => i !== index)
    setSettings(prev => ({ ...prev, customSections: updated }))
  }

  return (
    <div className="space-y-6">
      
      {/* ========== SEZIONI STANDARD (Professional + Enterprise) ========== */}
      <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
            <Sliders className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-blue-900 dark:text-blue-100">Sezioni Standard</h3>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              {isStarter && 'Upgrade al piano Professional per personalizzare le opzioni dei campi standard.'}
              {(isProfessional || isEnterprise) && 'Personalizza le opzioni dei campi multi-select del form (max 5 opzioni per campo).'}
            </p>
          </div>
        </div>
      </Card>

      {/* Lista Sezioni Standard */}
      {(isProfessional || isEnterprise) && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Campi Multi-Select</h2>
          <div className="space-y-3">
            {standardSections.map((section) => {
              const options = getStandardOptions(section.id)
              return (
                <div key={section.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{section.icon}</span>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">{section.label}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {options.length > 0 
                          ? `${options.length} opzioni configurate`
                          : 'Opzioni predefinite'}
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => openStandardModal(section)} 
                    className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg flex items-center gap-1"
                  >
                    <Edit2 className="h-4 w-4" />
                    <span className="text-sm">Modifica</span>
                  </button>
                </div>
              )
            })}
          </div>
        </Card>
      )}

      {/* Upgrade CTA per Starter - Sezioni Standard */}
      {isStarter && (
        <Card className="text-center py-6">
          <div className="w-12 h-12 mx-auto bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center mb-3">
            <Sliders className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Personalizza le opzioni</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto text-sm">
            Con il piano Professional puoi modificare le opzioni dei campi comportamento, compiti, attività e tempo libero.
          </p>
        </Card>
      )}

      {/* Divider */}
      <div className="border-t dark:border-gray-700"></div>

      {/* ========== SEZIONI PERSONALIZZATE (Solo Enterprise) ========== */}
      <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border-purple-200 dark:border-purple-800">
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
            <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="font-semibold text-purple-900 dark:text-purple-100">Sezioni Personalizzate</h3>
            <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
              {(isStarter || isProfessional) && 'Upgrade al piano Enterprise per creare sezioni personalizzate.'}
              {isEnterprise && `Crea sezioni aggiuntive per il form. Usate: ${customSections.length}/${maxSections}`}
            </p>
          </div>
        </div>
      </Card>

      {/* Lista Sezioni Personalizzate */}
      {isEnterprise && customSections.length > 0 && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Le tue sezioni</h2>
          <div className="space-y-3">
            {customSections.map((section, index) => (
              <div key={section.id || index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">{section.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {section.options?.length || 0} opzioni, {section.notes?.length || 0} note
                    {section.options?.some(o => o.linkedNote) && ' (+ note collegate)'}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button onClick={() => openEditModal(index)} className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg">
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => deleteSection(index)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Bottone Aggiungi Sezione Personalizzata */}
      {isEnterprise && customSections.length < maxSections && (
        <button onClick={openCreateModal} className="w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 hover:border-primary-500 hover:text-primary-600 transition-colors flex items-center justify-center space-x-2">
          <Plus className="h-5 w-5" />
          <span>Aggiungi sezione personalizzata</span>
        </button>
      )}

      {/* Upgrade CTA per Starter/Professional - Sezioni Personalizzate */}
      {(isStarter || isProfessional) && (
        <Card className="text-center py-6">
          <div className="w-12 h-12 mx-auto bg-purple-100 dark:bg-purple-900/50 rounded-full flex items-center justify-center mb-3">
            <FileText className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Sezioni Personalizzate</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto text-sm">
            Con il piano Enterprise puoi creare fino a 3 sezioni aggiuntive con opzioni e note personalizzate.
          </p>
        </Card>
      )}

      {/* ========== MODAL SEZIONE STANDARD ========== */}
      {standardModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">{standardModal.icon}</span>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {standardModal.label}
                </h3>
              </div>
              <button onClick={() => setStandardModal(null)} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1 space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {standardModal.description}. Massimo {maxOptionsPerField} opzioni.
              </p>

              {/* Lista opzioni */}
              <div className="space-y-2">
                {standardOptions.map((opt, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <span className="text-sm text-gray-900 dark:text-white">{opt}</span>
                    <button 
                      onClick={() => removeStandardOption(index)} 
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Aggiungi opzione */}
              {standardOptions.length < maxOptionsPerField && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tempStandardInput}
                    onChange={(e) => setTempStandardInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addStandardOption()}
                    placeholder="Nuova opzione..."
                    className="input flex-1"
                    maxLength={50}
                  />
                  <button 
                    onClick={addStandardOption}
                    disabled={!tempStandardInput.trim()}
                    className="btn-secondary px-3"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              )}

              <p className="text-xs text-gray-400">
                {standardOptions.length}/{maxOptionsPerField} opzioni
              </p>
            </div>

            <div className="p-4 border-t dark:border-gray-700 flex justify-between gap-2">
              <button 
                onClick={resetToDefaults}
                disabled={savingStandard}
                className="btn-secondary text-sm flex items-center gap-1"
                title="Ripristina le opzioni predefinite"
              >
                🔄 Ripristina default
              </button>
              <div className="flex gap-2">
                <button 
                  onClick={() => setStandardModal(null)} 
                  className="btn-secondary"
                  disabled={savingStandard}
                >
                  Annulla
                </button>
                <button 
                  onClick={saveStandardSection}
                  disabled={savingStandard}
                  className="btn-primary flex items-center gap-2"
                >
                  {savingStandard && <Loader2 className="h-4 w-4 animate-spin" />}
                  {savingStandard ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== MODAL SEZIONE PERSONALIZZATA ========== */}
      {sectionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {sectionModal.mode === 'create' ? 'Nuova Sezione' : 'Modifica Sezione'}
              </h3>
              <button 
                onClick={() => {
                  setSectionModal(null)
                  setSectionForm({ name: '', options: [], notes: [] })
                }} 
                disabled={savingSection}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1 space-y-4">
              {/* Nome sezione */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome sezione *</label>
                <input
                  type="text"
                  value={sectionForm.name}
                  onChange={(e) => setSectionForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Es: Alimentazione, Trasporto..."
                  className="input w-full"
                  maxLength={30}
                />
              </div>

              {/* Opzioni */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Opzioni selezionabili</label>
                  <span className="text-xs text-gray-500">{sectionForm.options.length}/5</span>
                </div>
                <div className="space-y-2">
                  {sectionForm.options.map(opt => (
                    <div key={opt.id} className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-900 dark:text-white">{opt.label}</span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => toggleLinkedNote(opt.id)}
                            className={`p-1 rounded ${opt.linkedNote ? 'text-primary-600 bg-primary-50' : 'text-gray-400 hover:text-gray-600'}`}
                            title="Aggiungi nota collegata"
                          >
                            <MessageSquare className="h-4 w-4" />
                          </button>
                          <button onClick={() => removeSectionOption(opt.id)} className="p-1 text-gray-400 hover:text-red-500">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {opt.linkedNote && (
                        <input
                          type="text"
                          value={opt.linkedNote}
                          onChange={(e) => updateLinkedNoteLabel(opt.id, e.target.value)}
                          placeholder="Label nota collegata..."
                          className="mt-2 input w-full text-sm"
                          maxLength={30}
                        />
                      )}
                    </div>
                  ))}
                </div>
                {sectionForm.options.length < 5 && getTotalElements() < 6 && (
                  <div className="flex gap-2 mt-2">
                    <input
                      type="text"
                      value={tempOptionInput}
                      onChange={(e) => setTempOptionInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          addSectionOption(tempOptionInput)
                          setTempOptionInput('')
                        }
                      }}
                      placeholder="Nuova opzione..."
                      className="input flex-1"
                      maxLength={30}
                    />
                    <button
                      onClick={() => {
                        addSectionOption(tempOptionInput)
                        setTempOptionInput('')
                      }}
                      disabled={!tempOptionInput.trim()}
                      className="btn-secondary px-3"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Note libere */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Campi note</label>
                  <span className="text-xs text-gray-500">{sectionForm.notes.length}/{getMaxNotes()}</span>
                </div>
                <div className="space-y-2">
                  {sectionForm.notes.map(note => (
                    <div key={note.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <span className="text-sm text-gray-900 dark:text-white">{note.label}</span>
                      <button onClick={() => removeSectionNote(note.id)} className="p-1 text-gray-400 hover:text-red-500">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {sectionForm.notes.length < getMaxNotes() && getTotalElements() < 6 && (
                  <div className="flex gap-2 mt-2">
                    <input
                      type="text"
                      value={tempNoteInput}
                      onChange={(e) => setTempNoteInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          addSectionNote(tempNoteInput)
                          setTempNoteInput('')
                        }
                      }}
                      placeholder="Nuova nota..."
                      className="input flex-1"
                      maxLength={30}
                    />
                    <button
                      onClick={() => {
                        addSectionNote(tempNoteInput)
                        setTempNoteInput('')
                      }}
                      disabled={!tempNoteInput.trim()}
                      className="btn-secondary px-3"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Anteprima */}
              {(sectionForm.options.length > 0 || sectionForm.notes.length > 0) && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-2 flex items-center gap-1">
                    <Eye className="h-3 w-3" /> Anteprima
                  </p>
                  <div className="space-y-1 text-sm">
                    {sectionForm.options.length > 0 && (
                      <p className="text-blue-600 dark:text-blue-400">
                        {sectionForm.options.map(o => o.label).join(', ')}
                      </p>
                    )}
                    {sectionForm.options.some(o => o.linkedNote) && (
                      <p className="text-xs text-blue-500">
                        + Note collegate: {sectionForm.options.filter(o => o.linkedNote).map(o => o.linkedNote).join(', ')}
                      </p>
                    )}
                    {sectionForm.notes.length > 0 && (
                      <p className="text-xs text-blue-500">
                        Campi note: {sectionForm.notes.map(n => n.label).join(', ')}
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-blue-500 mt-2">
                    Opzioni: {sectionForm.options.length}/5 | Note: {sectionForm.notes.length}/{getMaxNotes()} | Totale: {getTotalElements()}/6
                  </p>
                </div>
              )}
            </div>

            <div className="p-4 border-t dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => {
                  setSectionModal(null)
                  setSectionForm({ name: '', options: [], notes: [] })
                }}
                disabled={savingSection}
                className="btn-secondary"
              >
                Annulla
              </button>
              <button
                onClick={saveSection}
                disabled={savingSection || !sectionForm.name.trim() || (sectionForm.options.length === 0 && sectionForm.notes.length === 0)}
                className="btn-primary flex items-center gap-2"
              >
                {savingSection && <Loader2 className="h-4 w-4 animate-spin" />}
                {savingSection 
                  ? 'Salvataggio...' 
                  : (sectionModal.mode === 'create' ? 'Crea Sezione' : 'Salva Modifiche')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
